/**
 * PushOptInBanner — small Dashboard banner that prompts the user to enable
 * web push. Auto-hides once subscribed, dismissed, or unsupported.
 *
 * Never triggers the browser permission dialog on mount — that must come
 * from a user click (Chrome/Safari requirement), so we show a friendly card
 * with an "Enable" button and only prompt on click.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePush } from "@/lib/usePush";

const DISMISS_KEY = "push_prompt_dismissed_v1";

export default function PushOptInBanner() {
  const { supported, iosBlockedUntilInstalled, permission, subscribed, loading, subscribe } = usePush();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1"
  );

  // Hide the banner if:
  //  - Browser doesn't support Web Push (older iOS, insecure origin)
  //  - Already subscribed
  //  - User previously permanently denied
  //  - User dismissed
  if (!supported || subscribed || permission === "denied" || dismissed) {
    return null;
  }

  const onEnable = async () => {
    const r = await subscribe();
    if (r.ok) {
      toast.success("تم تفعيل الإشعارات! ستصلك أحدث الوصفات والدروس.");
    } else if (r.reason === "install-first") {
      toast.info("لتفعيل الإشعارات على iPhone، ثبّت التطبيق أولاً من زر «إضافة إلى الشاشة الرئيسية».", { duration: 6000 });
    } else if (r.reason === "denied") {
      toast.error("رفضت الإذن. يمكنك تفعيلها لاحقاً من إعدادات المتصفح.");
    } else {
      toast.error("تعذّر تفعيل الإشعارات — حاول مرة أخرى.");
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-4 sm:p-5 animate-fade-in-up"
      data-testid="push-optin-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 end-2 h-7 w-7 grid place-items-center rounded-full text-muted-foreground/60 hover:text-foreground hover:bg-foreground/5"
        aria-label="إخفاء"
        data-testid="push-optin-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-11 w-11 sm:h-12 sm:w-12 rounded-2xl bg-primary/15 text-primary grid place-items-center">
          <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-base sm:text-lg">فعّل الإشعارات</h3>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {iosBlockedUntilInstalled
              ? "ثبّت التطبيق على شاشتك الرئيسية أولاً، ثم فعّل الإشعارات لتصلك الوصفات والدروس الجديدة ورسائل المدرب فوراً."
              : "خلّي وصفات جديدة، دروس، ورسائل المدرب توصلك مباشرة على جهازك."}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              size="sm"
              onClick={onEnable}
              disabled={loading || iosBlockedUntilInstalled}
              className="gap-1.5 rounded-full"
              data-testid="push-optin-enable"
            >
              {iosBlockedUntilInstalled ? (
                <>
                  <Smartphone className="h-3.5 w-3.5" /> ثبّت التطبيق أولاً
                </>
              ) : (
                <>
                  <Bell className="h-3.5 w-3.5" /> {loading ? "جارٍ التفعيل..." : "تفعيل الإشعارات"}
                </>
              )}
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} className="rounded-full gap-1.5" data-testid="push-optin-later">
              <BellOff className="h-3.5 w-3.5" /> لاحقاً
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
